const basePath = "end-user-computing/";

export const endUserComputing = {
  id: 6,
  category: "End-User Computing",

  filters: {
    Brand: { type: "checkbox" },
    Model: { type: "checkbox" },
    Type: { type: "checkbox" },
    Processor: { type: "checkbox" },
    RAM: { type: "checkbox" },
    Storage: { type: "checkbox" },
    Display: { type: "checkbox" },
    "Operating System": { type: "checkbox" },
  },

  subcategories: [
    // ==========================
    // BUSINESS LAPTOPS
    // ==========================
    {
      name: "Dell Latitude 5450 Business Laptop",
      bestSeller: true,
      image: `${basePath}Dell_Latitude_5450.svg`,
      details: {
        Brand: "Dell",
        Model: "Latitude 5450",
        Type: "Business Laptop",
        Processor: "Intel Core Ultra 5 125U",
        RAM: "16 GB DDR5",
        Storage: "512 GB NVMe SSD",
        Display: '14" FHD (1920x1080) IPS',
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>The volume corporate laptop — built for fleet deployment rather than individual purchase.</li>
          <li>vPro management support allows remote provisioning and repair below the operating system.</li>
          <li>Serviceable design: memory and storage can be replaced without specialist tools.</li>
          <li>Available with a three-year ProSupport contract for fleet standardisation.</li>
        </ul>
      `,
    },
    {
      name: "HP ProBook 450 G10",
      bestSeller: true,
      image: `${basePath}HP_ProBook_450_G10.svg`,
      details: {
        Brand: "HP",
        Model: "ProBook 450 G10",
        Type: "Business Laptop",
        Processor: "Intel Core i5-1335U",
        RAM: "16 GB DDR4",
        Storage: "512 GB NVMe SSD",
        Display: '15.6" FHD (1920x1080)',
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>15.6" business laptop with a full numeric keypad — favoured by finance and admin teams.</li>
          <li>HP Wolf Security provides firmware-level protection and self-healing BIOS.</li>
          <li>Wide port selection including HDMI, USB-C and RJ45, so no dongle is required for most desks.</li>
          <li>A practical middle ground between consumer pricing and enterprise manageability.</li>
        </ul>
      `,
    },
    {
      name: "Lenovo ThinkPad E14 Gen 5",
      bestSeller: true,
      image: `${basePath}Lenovo_ThinkPad_E14_Gen5.svg`,
      details: {
        Brand: "Lenovo",
        Model: "ThinkPad E14 Gen 5",
        Type: "Business Laptop",
        Processor: "AMD Ryzen 5 7530U",
        RAM: "16 GB DDR4",
        Storage: "512 GB NVMe SSD",
        Display: '14" WUXGA (1920x1200)',
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>Entry point into the ThinkPad line, keeping the keyboard and durability standards at a lower price.</li>
          <li>16:10 display gives noticeably more vertical working space than a 16:9 panel.</li>
          <li>MIL-STD-810H tested for shock, humidity and temperature extremes.</li>
          <li>Good choice for organisations standardising on ThinkPad without ThinkPad X-series budgets.</li>
        </ul>
      `,
    },
    {
      name: "Lenovo ThinkPad X1 Carbon Gen 12",
      bestSeller: false,
      image: `${basePath}Lenovo_ThinkPad_X1_Carbon_Gen12.svg`,
      details: {
        Brand: "Lenovo",
        Model: "ThinkPad X1 Carbon Gen 12",
        Type: "Ultrabook",
        Processor: "Intel Core Ultra 7 155H",
        RAM: "32 GB LPDDR5x",
        Storage: "1 TB NVMe SSD",
        Display: '14" 2.8K OLED (2880x1800)',
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>Carbon-fibre chassis under 1.1 kg — the executive and consultant travel machine.</li>
          <li>Soldered LPDDR5x memory, so specify the RAM correctly at order time; it cannot be upgraded later.</li>
          <li>Neural processing unit for on-device AI features in Windows 11.</li>
          <li>Full vPro manageability for IT fleet control.</li>
        </ul>
      `,
    },
    {
      name: "Apple MacBook Air 13\" M3",
      bestSeller: false,
      image: `${basePath}Apple_MacBook_Air_M3.svg`,
      details: {
        Brand: "Apple",
        Model: 'MacBook Air 13" M3',
        Type: "Ultrabook",
        Processor: "Apple M3 (8-core CPU, 10-core GPU)",
        RAM: "16 GB unified memory",
        Storage: "512 GB SSD",
        Display: '13.6" Liquid Retina (2560x1664)',
        "Operating System": "macOS",
      },
      about: `
        <ul>
          <li>Fanless design — silent operation, which matters in studios and client-facing settings.</li>
          <li>Around 18 hours of battery life on typical office workloads.</li>
          <li>Supported in Microsoft Intune and Jamf for managed enterprise deployment.</li>
          <li>Memory and storage are not upgradeable after purchase — specify the configuration up front.</li>
        </ul>
      `,
    },
    {
      name: "ASUS ExpertBook B1 B1502",
      bestSeller: false,
      image: `${basePath}ASUS_ExpertBook_B1.svg`,
      details: {
        Brand: "ASUS",
        Model: "ExpertBook B1 B1502",
        Type: "Business Laptop",
        Processor: "Intel Core i5-1235U",
        RAM: "16 GB DDR4",
        Storage: "512 GB NVMe SSD",
        Display: '15.6" FHD (1920x1080)',
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>Cost-effective business laptop with military-grade durability testing.</li>
          <li>Two SODIMM slots and two M.2 slots, so it can be upgraded in service rather than replaced.</li>
          <li>Physical webcam shield and fingerprint reader as standard.</li>
          <li>Suits budget-conscious fleet rollouts where serviceability matters more than weight.</li>
        </ul>
      `,
    },

    // ==========================
    // MOBILE WORKSTATIONS
    // ==========================
    {
      name: "Dell Precision 3591 Mobile Workstation",
      bestSeller: false,
      image: `${basePath}Dell_Precision_3591.svg`,
      details: {
        Brand: "Dell",
        Model: "Precision 3591",
        Type: "Mobile Workstation",
        Processor: "Intel Core Ultra 7 155H",
        RAM: "32 GB DDR5",
        Storage: "1 TB NVMe SSD",
        Display: '15.6" FHD+ (1920x1200)',
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>NVIDIA RTX A500 professional graphics with ISV certification for CAD and BIM applications.</li>
          <li>Certified for SolidWorks, AutoCAD, Revit and Siemens NX — the certification is the point, not the raw specification.</li>
          <li>Supports up to 64 GB of memory across two slots.</li>
          <li>Pairs naturally with the AutoCAD and SolidWorks licences in our software catalogue.</li>
        </ul>
      `,
    },
    {
      name: "HP ZBook Firefly 14 G11",
      bestSeller: false,
      image: `${basePath}HP_ZBook_Firefly_14_G11.svg`,
      details: {
        Brand: "HP",
        Model: "ZBook Firefly 14 G11",
        Type: "Mobile Workstation",
        Processor: "Intel Core Ultra 7 165H",
        RAM: "32 GB DDR5",
        Storage: "1 TB NVMe SSD",
        Display: '14" WUXGA (1920x1200)',
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>Workstation certification in a 1.4 kg chassis — for engineers who travel.</li>
          <li>NVIDIA RTX A500 graphics with professional application certification.</li>
          <li>HP Sure Start firmware protection and Sure View optional privacy screen.</li>
          <li>Choose this over the Precision 3591 when weight matters more than screen size.</li>
        </ul>
      `,
    },

    // ==========================
    // DESKTOPS
    // ==========================
    {
      name: "Dell OptiPlex 7010 Micro Desktop",
      bestSeller: true,
      image: `${basePath}Dell_OptiPlex_7010_Micro.svg`,
      details: {
        Brand: "Dell",
        Model: "OptiPlex 7010 Micro",
        Type: "Micro Desktop",
        Processor: "Intel Core i5-13500T",
        RAM: "16 GB DDR4",
        Storage: "512 GB NVMe SSD",
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>1.1 litre chassis that mounts behind a monitor or under a desk — reclaims the desk footprint entirely.</li>
          <li>Low-power T-series processor keeps heat and noise down in open-plan offices.</li>
          <li>vPro manageability for remote deployment across a fleet.</li>
          <li>The standard call for call centres, reception desks and hot-desking floors.</li>
        </ul>
      `,
    },
    {
      name: "HP EliteDesk 800 G9 SFF",
      bestSeller: false,
      image: `${basePath}HP_EliteDesk_800_G9_SFF.svg`,
      details: {
        Brand: "HP",
        Model: "EliteDesk 800 G9 SFF",
        Type: "Small Form Factor Desktop",
        Processor: "Intel Core i7-13700",
        RAM: "16 GB DDR5",
        Storage: "1 TB NVMe SSD",
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>Small form factor keeps a PCIe slot and extra drive bays that a micro desktop gives up.</li>
          <li>Full-power desktop processor rather than a mobile part — sustained performance under load.</li>
          <li>Tool-less chassis for quick memory and storage servicing.</li>
          <li>Fits users who need real compute at a desk but not a full tower.</li>
        </ul>
      `,
    },
    {
      name: "Lenovo ThinkCentre M70q Gen 4 Tiny",
      bestSeller: false,
      image: `${basePath}Lenovo_ThinkCentre_M70q_Gen4.svg`,
      details: {
        Brand: "Lenovo",
        Model: "ThinkCentre M70q Gen 4",
        Type: "Micro Desktop",
        Processor: "Intel Core i5-13400T",
        RAM: "16 GB DDR4",
        Storage: "512 GB NVMe SSD",
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>1 litre chassis with VESA mounting for monitor-back or wall installation.</li>
          <li>Supports three independent displays from the integrated graphics.</li>
          <li>Optional serial and legacy ports for retail and industrial deployments.</li>
          <li>Widely deployed as a digital signage and kiosk driver as well as an office desktop.</li>
        </ul>
      `,
    },

    // ==========================
    // WORKSTATIONS
    // ==========================
    {
      name: "Dell Precision 3680 Tower Workstation",
      bestSeller: false,
      image: `${basePath}Dell_Precision_3680_Tower.svg`,
      details: {
        Brand: "Dell",
        Model: "Precision 3680 Tower",
        Type: "Tower Workstation",
        Processor: "Intel Core i7-14700",
        RAM: "32 GB DDR5",
        Storage: "1 TB NVMe SSD",
        "Operating System": "Windows 11 Pro",
      },
      about: `
        <ul>
          <li>Entry tower workstation supporting up to 128 GB of memory and professional graphics up to RTX 4000 Ada.</li>
          <li>ISV certified for the major CAD, rendering and simulation applications.</li>
          <li>Multiple full-length PCIe slots and internal drive bays for expansion over the machine's life.</li>
          <li>The right base for the KeyShot, V-Ray and 3ds Max licences in our software catalogue.</li>
        </ul>
      `,
    },
    {
      name: "HP Z4 G5 Workstation",
      bestSeller: false,
      image: `${basePath}HP_Z4_G5_Workstation.svg`,
      details: {
        Brand: "HP",
        Model: "Z4 G5",
        Type: "Tower Workstation",
        Processor: "Intel Xeon w5-2445",
        RAM: "64 GB DDR5 ECC",
        Storage: "2 TB NVMe SSD",
        "Operating System": "Windows 11 Pro for Workstations",
      },
      about: `
        <ul>
          <li>Xeon W processor with ECC memory — for work where a silent bit-flip is unacceptable.</li>
          <li>Scales to 512 GB of memory and dual professional GPUs.</li>
          <li>Suited to simulation, large-assembly CAD, and video finishing.</li>
          <li>The step up from a Precision 3680 when datasets outgrow non-ECC memory.</li>
        </ul>
      `,
    },

    // ==========================
    // THIN CLIENTS
    // ==========================
    {
      name: "Dell Wyse 5070 Thin Client",
      bestSeller: false,
      image: `${basePath}Dell_Wyse_5070.svg`,
      details: {
        Brand: "Dell",
        Model: "Wyse 5070",
        Type: "Thin Client",
        Processor: "Intel Celeron J4105",
        RAM: "8 GB DDR4",
        Storage: "64 GB eMMC",
        "Operating System": "ThinOS / Windows 10 IoT",
      },
      about: `
        <ul>
          <li>Endpoint for VDI environments — no local data, so a stolen or failed unit carries no risk.</li>
          <li>ThinOS has no published malware, which removes endpoint antivirus from the estate.</li>
          <li>Supports Citrix, VMware Horizon and Microsoft AVD sessions.</li>
          <li>Typical replacement cycle is far longer than a PC because the workload sits in the datacenter.</li>
        </ul>
      `,
    },
    {
      name: "HP t640 Thin Client",
      bestSeller: false,
      image: `${basePath}HP_t640_Thin_Client.svg`,
      details: {
        Brand: "HP",
        Model: "t640",
        Type: "Thin Client",
        Processor: "AMD Ryzen R1505G",
        RAM: "8 GB DDR4",
        Storage: "32 GB flash",
        "Operating System": "HP ThinPro / Windows 10 IoT",
      },
      about: `
        <ul>
          <li>Drives up to four 4K displays — for trading floors, control rooms and dispatch desks.</li>
          <li>Fanless operation with no moving parts to fail.</li>
          <li>Managed centrally through HP Device Manager.</li>
          <li>Chosen over the Wyse 5070 when multi-monitor output is the requirement.</li>
        </ul>
      `,
    },

    // ==========================
    // DISPLAYS & ACCESSORIES
    // ==========================
    {
      name: "Dell UltraSharp U2723QE 27\" 4K Monitor",
      bestSeller: true,
      image: `${basePath}Dell_UltraSharp_U2723QE.svg`,
      details: {
        Brand: "Dell",
        Model: "UltraSharp U2723QE",
        Type: "Monitor",
        Display: '27" 4K UHD (3840x2160) IPS Black',
        Compatibility: "USB-C, HDMI, DisplayPort",
      },
      about: `
        <ul>
          <li>Single USB-C cable carries video, data and 90 W of laptop charging — one cable to the desk.</li>
          <li>Built-in RJ45 gives a docked laptop wired networking without a separate dock.</li>
          <li>IPS Black panel roughly doubles contrast over a conventional IPS display.</li>
          <li>Height, tilt, swivel and pivot adjustment as standard.</li>
        </ul>
      `,
    },
    {
      name: "HP E24 G5 24\" FHD Monitor",
      bestSeller: false,
      image: `${basePath}HP_E24_G5_Monitor.svg`,
      details: {
        Brand: "HP",
        Model: "E24 G5",
        Type: "Monitor",
        Display: '23.8" FHD (1920x1080) IPS',
        Compatibility: "HDMI, DisplayPort, VGA, USB hub",
      },
      about: `
        <ul>
          <li>The standard corporate desk monitor — specified for fleet consistency rather than headline specification.</li>
          <li>Full ergonomic stand with 150 mm height adjustment and pivot to portrait.</li>
          <li>Built-in USB hub reduces cable clutter at the desk.</li>
          <li>Low blue light mode for extended office use.</li>
        </ul>
      `,
    },
    {
      name: "Dell WD19S 180W Docking Station",
      bestSeller: true,
      image: `${basePath}Dell_WD19S_Dock.svg`,
      details: {
        Brand: "Dell",
        Model: "WD19S",
        Type: "Docking Station",
        Compatibility: "USB-C laptops (Dell and third-party)",
      },
      about: `
        <ul>
          <li>Single USB-C connection for dual 4K displays, wired network, USB peripherals and 130 W laptop charging.</li>
          <li>Modular power delivery — the power module can be upgraded separately from the dock.</li>
          <li>MAC address pass-through supports PXE boot and network access control policies.</li>
          <li>The practical enabler for hot-desking: one cable per desk, any laptop.</li>
        </ul>
      `,
    },
    {
      name: "Logitech MK540 Wireless Keyboard & Mouse",
      bestSeller: false,
      image: `${basePath}Logitech_MK540_Combo.svg`,
      details: {
        Brand: "Logitech",
        Model: "MK540 Advanced",
        Type: "Input Peripherals",
        Compatibility: "Windows, macOS, Linux (USB receiver)",
      },
      about: `
        <ul>
          <li>Single unifying USB receiver drives both keyboard and mouse, freeing a port.</li>
          <li>Three-year keyboard battery life and 18 months on the mouse — very few support calls.</li>
          <li>Full-size layout with numeric keypad and a contoured palm rest.</li>
          <li>Ordered in volume alongside desktop and thin client rollouts.</li>
        </ul>
      `,
    },
  ],
};
